import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_URL } from '../../../../config.js';
import { toApiError } from '../../../../lib/api-error.js';
import type {
  TestSignInActivityResponse,
  TestSignInClient,
  TestSignInGetResponse,
} from './types';

export const TEST_SIGN_IN_QUERY_KEY = ['test-sign-in'] as const;

export function useTestSignInQuery(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: TEST_SIGN_IN_QUERY_KEY,
    queryFn: async (): Promise<TestSignInGetResponse> => {
      const res = await fetch(`${API_URL}/test-sign-in`, { credentials: 'include' });
      if (!res.ok) throw await toApiError(res, 'Failed to load test sign-in');
      return res.json();
    },
    enabled: options?.enabled ?? true,
  });
}

export function useTestSignInActivityQuery(credentialId?: string | null, limit = 25) {
  return useQuery({
    queryKey: ['test-sign-in', 'activity', credentialId ?? null, limit],
    queryFn: async (): Promise<TestSignInActivityResponse> => {
      const params = new URLSearchParams();
      if (credentialId) params.set('credentialId', credentialId);
      params.set('limit', String(limit));
      const res = await fetch(`${API_URL}/test-sign-in/activity?${params.toString()}`, {
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to load activity');
      return res.json();
    },
  });
}

export function useCreateProviderMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (emailDomain: string) => {
      const res = await fetch(`${API_URL}/test-sign-in/provider`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ emailDomain }),
      });
      if (!res.ok) throw await toApiError(res, 'Failed to set up test sign-in');
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: TEST_SIGN_IN_QUERY_KEY }),
  });
}

export function useUpdateProviderMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { emailDomain?: string; disabled?: boolean }) => {
      const res = await fetch(`${API_URL}/test-sign-in/provider`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw await toApiError(res, 'Failed to update test sign-in');
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: TEST_SIGN_IN_QUERY_KEY }),
  });
}

export function useCreateClientMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      propertyId: string;
      redirectUris: string[];
    }): Promise<{ ok: boolean; data: { client: TestSignInClient; clientSecret: string } }> => {
      const res = await fetch(`${API_URL}/test-sign-in/clients`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw await toApiError(res, 'Failed to add application');
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: TEST_SIGN_IN_QUERY_KEY }),
  });
}

export function useUpdateClientMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      clientId: string;
      redirectUris?: string[];
      disabled?: boolean;
    }) => {
      const { clientId, ...body } = input;
      const res = await fetch(`${API_URL}/test-sign-in/clients/${clientId}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw await toApiError(res, 'Failed to update application');
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: TEST_SIGN_IN_QUERY_KEY }),
  });
}

export function useRotateClientSecretMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      clientId: string
    ): Promise<{ ok: boolean; data: { client: TestSignInClient; clientSecret: string } }> => {
      const res = await fetch(`${API_URL}/test-sign-in/clients/${clientId}/rotate-secret`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to rotate secret');
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: TEST_SIGN_IN_QUERY_KEY }),
  });
}
